import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import InputCheckbox from "../../../components/input/input-checkbox.jsx";
import Button from "../../../components/button/button.jsx";
import {useEffect, useState} from "react";
import useFetchData from "./hooks/use-fetch-data.js";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import CompressionService from "../../../services/web/compression-service.js";
import {toast} from "react-toastify";
import ProjectUtils from "../../../utils/project-utils.js";
import {FaArrowCircleDown, FaArrowsAlt, FaClock, FaExchangeAlt, FaFile, FaTag} from "react-icons/fa";

function PreviewImage({ title, src }) {
    return (
        <div className="flex-1 min-w-65">
            <h5 className="fonte-semibold mb-2 text-center">{title}</h5>
            <img src={src} className="max-w-full h-auto rounded border shadow-sm"/>
        </div>
    );
}

export default function Compression() {
    const fetchData = useFetchData();
    const [preview, setPreview] = useState(null);
    const queryClient = useQueryClient();

    const [form, setForm] = useState({
        originalDays: 0,
        compression: 10,
        dimension: null,
        lane: null,
        enabled: false,
    });

    const { data: config, isLoading } = useQuery({
        queryKey: ['config'],
        queryFn: CompressionService.getConfig,
    });

    const previewMutation = useMutation({
        mutationFn: (formData) => CompressionService.getNewImage(formData),
        onSuccess: (data) => {
            if (data?.warn) {
                setPreview({ warn: data.warn });
                return;
            }
            setPreview(data);
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || "Ocorreu um erro durante o carregamento das imagens.";
            ProjectUtils.swalErrorMessage("", msg, "Ok");
        },
    });

    useEffect(() => {
        if (!config) return;
        setForm({
            originalDays: Number(config.originalDays),
            compression: Number(config.compression),
            enabled: config.enabledCompression,
            dimension: fetchData.dimensions.find(o => String(o.value) === String(config.dimension)),
            lane: fetchData.lanes.find(o => String(o.value) === String(config.lane)),
        });
    }, [config, fetchData.dimensions, fetchData.lanes]);

    const buildFormData = (currentForm = form) => {
        const fd = new FormData();

        fd.append("originalDays", String(currentForm.originalDays));
        fd.append("compression", String(currentForm.compression));
        fd.append("dimension", String(currentForm.dimension?.value));
        fd.append("lane", String(currentForm.lane?.value));

        if (currentForm.enabled) {
            fd.append("enabledCompression", "true");
        }

        return fd;
    };

    const runPreview = (currentForm = form) => {
        if (
            Number(currentForm.compression) < 10 ||
            Number(currentForm.compression) > 100 ||
            !currentForm.dimension ||
            !currentForm.lane ||
            !currentForm.enabled
        ) {
            return;
        }

        previewMutation.mutate(buildFormData(currentForm));
    };

    useEffect(() => {
        if (!config) return;

        if (!form.enabled) {
            setPreview(null);
            return;
        }

        if (!form.dimension || !form.lane) return;

        runPreview(form);
    }, [config, form.lane, form.dimension, form.enabled]);

    const text = (v) => String(v?.value ?? v?.label ?? v ?? "").trim();

    const validate = () => {
        if (!text(form.originalDays)) return 'O campo "Dias originais" é obrigatório';
        if (!text(form.lane)) return 'O campo "Câmera de pré-visualização" é obrigatório';
        if (!text(form.dimension)) return 'O campo "Largura (px)" é obrigatório';
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            ProjectUtils.swalErrorMessage("", err, "Ok");
            return;
        }
        try {
            await CompressionService.save(buildFormData(form));
            await queryClient.invalidateQueries({ queryKey: ["config"] });

            if (!form.enabled) {
                setPreview(null);
            } else {
                runPreview(form);
            }

            toast.success("Salvo com sucesso");
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "Não foi possível salvar. Tente novamente.";

            ProjectUtils.swalErrorMessage("", msg, "Ok");
        }
    };

    const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Compressão de imagens</Template.Title>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <InputForm
                                id="originalDays"
                                label="Dias originais *"
                                type="number"
                                classNameWrapper="flex-1"
                                value={form.originalDays}
                                onChange={(e) => updateField('originalDays', Math.max(0, Number(e.target.value)))}
                            />
                            <InputForm
                                id="compression"
                                label="Qualidade (%) *"
                                type="number"
                                classNameWrapper="flex-1"
                                min={10}
                                max={100}
                                value={form.compression}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    updateField('compression', value);
                                }}
                                onBlur={(e) => {
                                    const value = Number(e.target.value);
                                    if (!e.target.value) {updateField('compression', 10); return}
                                    updateField('compression', Math.max(10, Math.min(100, value)))
                                }}
                            />
                            <SelectComponent
                                id="dimension"
                                label="Largura (px) *"
                                type="select"
                                classNameWrapper="flex-1"
                                options={fetchData.dimensions}
                                value={form.dimension}
                                onChange={(val) => updateField('dimension', val)}
                            />
                            <SelectComponent
                                id="lane"
                                label="Câmera de pré-visualização *"
                                type="select"
                                classNameWrapper="flex-3"
                                options={fetchData.lanes}
                                value={form.lane}
                                onChange={(val) => updateField('lane', val)}
                            />
                        </div>
                        <div className="flex pt-4">
                            <InputCheckbox
                                id="enabledCompression"
                                label="Habilitado"
                                checked={form.enabled}
                                onChange={(e) => updateField('enabled', e.target.checked)}
                            />
                        </div>
                    </Card.Body>
                </Card>
                {preview?.warn ? (
                    <Card className="mt-2">
                        <Card.Body>
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <strong>{preview.warn}</strong>
                            </div>
                        </Card.Body>
                    </Card>
                ) : preview ? (
                    <Card className="mt-2">
                        <Card.Body>
                            <div className="space-y-3">
                                <div className="text-xs items-center justify-center text-zinc-600 flex flex-wrap gap-2">
                                    <FaClock /><span>Data de início: {preview.fromDate}</span>
                                    <FaArrowsAlt /> <span>Dimensão atual: {preview.currentDimW} x {preview.currentDimH}</span>
                                    <FaArrowsAlt /><span>Ganho aprox.: {preview.percent}</span>
                                    <FaTag /><span>Nova dimensão: {preview.newDimW} x {preview.newDimH}</span>
                                    <FaFile /> <span>Compressão atual: {preview.currentCompression}</span>
                                    <FaArrowCircleDown /> <span>Tamanho atual: {preview.originalSize}</span>
                                    <FaExchangeAlt /> <span>Tamanho reduzido: {preview.newSize}</span>
                                </div>
                                <div className="flex gap-4 flex-wrap">
                                    <PreviewImage title="Original" src={preview.imageOriginal} />
                                    <PreviewImage title="Nova Imagem" src={preview.imagePreview} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ) : null}
                <Card className="mt-2">
                    <Card.Body>
                        <div className="flex justify-end gap-2">
                            <Button onClick={handleSave} actionFeedbackType='save'>Salvar</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}